import { type AnyObject } from 'antd/lib/_util/type';
import { defer, from, map, type Observable, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { type ApiHookType } from '../types';
import utils from '../utils/utils';

/**
 * Functions to load data
 */

/**
 * uses GET method to fetch data
 * @param {string} url
 * @param {object} parameters
 * @param {object} headers
 * @param {object} asBlob
 */
function get(
  url: string,
  parameters: AnyObject,
  headers: HeadersInit,
  asBlob: boolean = false
): Observable<any> {
  // Create URL with parameters
  const urlObj = new URL(process.env.REACT_APP_API_BASE_URL + url);
  if (parameters) {
    utils.setSearchParams(urlObj, parameters);
  }
  // Perform request
  return defer(
    async () =>
      await fetch(urlObj, {
        method: 'GET',
        headers,
      })
  ).pipe(
    switchMap((response) =>
      asBlob && response.ok
        ? from(response.blob()).pipe(
            map((content) => ({
              filename: response.headers.get('Content-Disposition')?.split('"')[1],
              blob: content,
            }))
          )
        : response.ok
        ? from(response.status === 204 ? Promise.resolve({}) : response.json())
        : from(response.json()).pipe(switchMap((json) => throwError(() => new Error(json.error))))
    )
  );
}

/**
 * uses PUT method to update data
 *
 * @param {*} url
 * @param {*} data
 * @param {*} parameters
 * @param {*} headers
 */
function put(
  url: string,
  data: any,
  parameters: Record<string, unknown>,
  headers: HeadersInit
): Observable<any> {
  // Create URL with parameters
  const urlObj = new URL(process.env.REACT_APP_API_BASE_URL + url);
  if (parameters) {
    utils.setSearchParams(urlObj, parameters);
  }
  // Perform request
  return defer(
    async () =>
      await fetch(urlObj, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
      })
  ).pipe(
    switchMap((response) =>
      response.ok
        ? from(response.status === 204 ? Promise.resolve({}) : response.json())
        : from(response.json()).pipe(switchMap((json) => throwError(() => new Error(json.error))))
    )
  );
}

/**
 * uses PATCH method to update data
 *
 * @param {*} url
 * @param {*} data
 * @param {*} parameters
 * @param {*} headers
 */
function patch(
  url: string,
  data: any,
  parameters: Record<string, unknown>,
  headers: HeadersInit
): Observable<any> {
  // Create URL with parameters
  const urlObj = new URL(process.env.REACT_APP_API_BASE_URL + url);
  if (parameters) {
    utils.setSearchParams(urlObj, parameters);
  }
  // Perform request
  return defer(
    async () =>
      await fetch(urlObj, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(data),
      })
  ).pipe(
    switchMap((response) =>
      response.ok
        ? from(response.status === 204 ? Promise.resolve({}) : response.json())
        : from(response.json()).pipe(switchMap((json) => throwError(() => new Error(json.error))))
    )
  );
}

/**
 * uses POST method to insert data
 *
 * @param {*} url
 * @param {*} data
 * @param {*} parameters
 * @param {*} headers
 * @param {*} asBlob Bool flag to return the response as blob instead of json
 */
function post(
  url: string,
  data: any,
  parameters: Record<string, unknown>,
  headers: HeadersInit,
  asBlob: boolean = false
): Observable<any> {
  // Create URL with parameters
  const urlObj = new URL(process.env.REACT_APP_API_BASE_URL + url);
  if (parameters) {
    utils.setSearchParams(urlObj, parameters);
  }
  // Perform request
  return defer(
    async () =>
      await fetch(urlObj, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      })
  ).pipe(
    switchMap((response) =>
      asBlob && response.ok
        ? from(response.blob()).pipe(
            map((content) => ({
              filename: response.headers.get('Content-Disposition')?.split('"')[1],
              blob: content,
            }))
          )
        : response.ok
        ? from(response.status === 204 ? Promise.resolve({}) : response.json())
        : from(response.json()).pipe(switchMap((json) => throwError(() => new Error(json.error))))
    )
  );
}

/**
 * uses DELETE method to delete data
 *
 * @param {*} url
 * @param {*} data
 * @param {*} parameters
 * @param {*} headers
 */
function delete_(
  url: string,
  data: any,
  parameters: Record<string, unknown>,
  headers: HeadersInit
): Observable<any> {
  // Create URL with parameters
  const urlObj = new URL(process.env.REACT_APP_API_BASE_URL + url);
  if (parameters) {
    utils.setSearchParams(urlObj, parameters);
  }
  // Perform request
  return defer(
    async () =>
      await fetch(urlObj, {
        method: 'DELETE',
        body: JSON.stringify(data),
        headers,
      })
  ).pipe(
    switchMap((response) =>
      response.ok
        ? from(response.status === 204 ? Promise.resolve({}) : response.json())
        : from(response.json()).pipe(switchMap((json) => throwError(() => new Error(json.error))))
    )
  );
}

const useApi = (): ApiHookType => ({
  get,
  post,
  put,
  patch,
  delete: delete_,
});
export default useApi;
